import { useRef, useCallback, useEffect, useState } from 'react';
import { trackVideoEvent } from '@/lib/posthog';
import { getPageContext } from '@/lib/posthog-helpers';

export interface PostHogVideoTrackingOptions {
    /**
     * Video title for tracking
     */
    videoTitle?: string;

    /**
     * Video provider (e.g., 'vimeo', 'youtube', 'html5')
     */
    videoProvider?: string;

    /**
     * Video URL
     */
    videoUrl?: string;

    /**
     * Progress milestones to track (e.g., [25, 50, 75, 100])
     */
    progressMilestones?: number[];

    /**
     * Enable automatic tracking
     */
    autoTrack?: boolean;

    /**
     * Is this video set to autoplay?
     */
    isAutoplay?: boolean;

    /**
     * Custom properties to include with all video events
     */
    customProperties?: Record<string, any>;
}

/**
 * Hook for comprehensive video interaction tracking with progress milestones
 *
 * Usage:
 * const videoRef = useRef<HTMLVideoElement>(null);
 * usePostHogVideoTracking(videoRef, {
 *   videoTitle: 'Product Demo',
 *   progressMilestones: [25, 50, 75, 100]
 * });
 */
export const usePostHogVideoTracking = (
    videoRef: React.RefObject<HTMLVideoElement>,
    options: PostHogVideoTrackingOptions = {}
) => {
    const {
        videoTitle,
        videoProvider = 'html5',
        videoUrl,
        progressMilestones = [25, 50, 75, 100],
        autoTrack = true,
        isAutoplay = false,
        customProperties = {},
    } = options;

    const milestonesTracked = useRef<Set<number>>(new Set());
    const isPlaying = useRef<boolean>(false);
    const lastTrackedTime = useRef<number>(0);

    /**
     * Get video metadata
     */
    const getVideoMeta = useCallback(() => {
        const video = videoRef.current;
        return {
            video_title: videoTitle,
            video_provider: videoProvider,
            video_url: videoUrl || video?.src,
            video_duration: video?.duration || 0,
            playback_type: isAutoplay ? 'autoplay' : 'manual',
            ...customProperties,
            ...getPageContext(),
        };
    }, [videoTitle, videoProvider, videoUrl, videoRef, isAutoplay, customProperties]);

    /**
     * Track video start
     */
    const trackVideoStart = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        const eventAction = isAutoplay ? 'autoplay_started' : 'started';
        trackVideoEvent(eventAction, {
            ...getVideoMeta(),
            video_current_time: video.currentTime,
            video_percent: 0,
        });

        isPlaying.current = true;
    }, [getVideoMeta, videoRef, isAutoplay]);

    /**
     * Track video progress
     */
    const trackVideoProgress = useCallback((percent: number) => {
        const video = videoRef.current;
        if (!video) return;

        trackVideoEvent('progress', {
            ...getVideoMeta(),
            video_current_time: video.currentTime,
            video_percent: percent,
        });
    }, [getVideoMeta, videoRef]);

    /**
     * Track video completion
     */
    const trackVideoComplete = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        trackVideoEvent('completed', {
            ...getVideoMeta(),
            video_current_time: video.currentTime,
            video_percent: 100,
        });

        isPlaying.current = false;
    }, [getVideoMeta, videoRef]);

    /**
     * Track video pause
     */
    const trackVideoPause = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        trackVideoEvent('paused', {
            ...getVideoMeta(),
            video_current_time: video.currentTime,
            video_percent: (video.currentTime / video.duration) * 100,
        });
    }, [getVideoMeta, videoRef]);

    /**
     * Track video resume
     */
    const trackVideoResume = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        trackVideoEvent('resumed', {
            ...getVideoMeta(),
            video_current_time: video.currentTime,
            video_percent: (video.currentTime / video.duration) * 100,
        });
    }, [getVideoMeta, videoRef]);

    /**
     * Handle video play event
     */
    const handlePlay = useCallback(() => {
        if (!isPlaying.current) {
            trackVideoStart();
        } else {
            trackVideoResume();
        }
    }, [trackVideoStart, trackVideoResume]);

    /**
     * Handle video pause event
     */
    const handlePause = useCallback(() => {
        if (isPlaying.current) {
            trackVideoPause();
            isPlaying.current = false;
        }
    }, [trackVideoPause]);

    /**
     * Handle video ended event
     */
    const handleEnded = useCallback(() => {
        trackVideoComplete();
    }, [trackVideoComplete]);

    /**
     * Handle video time update (for progress tracking)
     */
    const handleTimeUpdate = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.duration || !isPlaying.current) return;

        const currentTime = video.currentTime;
        const duration = video.duration;
        const percentComplete = (currentTime / duration) * 100;

        // Check if we've reached any new milestones
        progressMilestones.forEach(milestone => {
            if (
                percentComplete >= milestone &&
                !milestonesTracked.current.has(milestone)
            ) {
                milestonesTracked.current.add(milestone);
                trackVideoProgress(milestone);
            }
        });

        lastTrackedTime.current = currentTime;
    }, [videoRef, progressMilestones, trackVideoProgress]);

    /**
     * Reset tracking state
     */
    const resetTracking = useCallback(() => {
        milestonesTracked.current.clear();
        isPlaying.current = false;
        lastTrackedTime.current = 0;
    }, []);

    /**
     * Set up event listeners
     */
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !autoTrack) return;

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [videoRef, autoTrack, handlePlay, handlePause, handleEnded, handleTimeUpdate]);

    /**
     * Reset tracking when video source changes
     */
    useEffect(() => {
        resetTracking();
    }, [videoUrl, resetTracking]);

    return {
        trackVideoStart,
        trackVideoProgress,
        trackVideoComplete,
        trackVideoPause,
        trackVideoResume,
        resetTracking,
    };
};

/**
 * Hook for Vimeo player tracking
 * Works with both autoplay and manual play scenarios
 * Handles player not being ready initially
 *
 * Usage:
 * const playerRef = useRef(null);
 * usePostHogVimeoTracking(playerRef, {
 *   videoTitle: 'Hero Video',
 *   videoUrl: 'https://vimeo.com/1234567',
 *   isAutoplay: true
 * });
 */
export const usePostHogVimeoTracking = (
    playerRef: React.RefObject<any>, // Vimeo Player ref
    options: Omit<PostHogVideoTrackingOptions, 'videoProvider'> = {}
) => {
    const {
        videoTitle,
        videoUrl,
        progressMilestones = [25, 50, 75, 100],
        autoTrack = true,
        isAutoplay = false,
        customProperties = {},
    } = options;

    const milestonesTracked = useRef<Set<number>>(new Set());
    const duration = useRef<number>(0);
    const wasPlaying = useRef<boolean>(false);
    const [isTracking, setIsTracking] = useState(false);

    const getVideoMeta = useCallback(() => ({
        video_title: videoTitle,
        video_provider: 'vimeo',
        video_url: videoUrl,
        video_duration: duration.current,
        playback_type: isAutoplay ? 'autoplay' : 'manual',
        ...customProperties,
        ...getPageContext(),
    }), [videoTitle, videoUrl, isAutoplay, customProperties]);

    const trackVideoStart = useCallback(() => {
        const eventAction = isAutoplay ? 'autoplay_started' : 'started';
        trackVideoEvent(eventAction, {
            ...getVideoMeta(),
            video_percent: 0,
        });
        console.log(`[PostHog] Video ${isAutoplay ? 'autoplay' : 'manual play'}:`, videoTitle);
    }, [getVideoMeta, isAutoplay, videoTitle]);

    const trackVideoProgress = useCallback((percent: number, currentTime: number) => {
        trackVideoEvent('progress', {
            ...getVideoMeta(),
            video_current_time: currentTime,
            video_percent: percent,
        });
        console.log(`[PostHog] Video ${percent}% milestone:`, videoTitle);
    }, [getVideoMeta, videoTitle]);

    const trackVideoComplete = useCallback(() => {
        trackVideoEvent('completed', {
            ...getVideoMeta(),
            video_percent: 100,
        });
        console.log('[PostHog] Video ended:', videoTitle);
    }, [getVideoMeta, videoTitle]);

    const trackVideoPause = useCallback((currentTime: number, percent: number) => {
        trackVideoEvent('paused', {
            ...getVideoMeta(),
            video_current_time: currentTime,
            video_percent: percent,
        });
        console.log('[PostHog] Video paused:', videoTitle);
    }, [getVideoMeta, videoTitle]);

    const trackVideoResume = useCallback((currentTime: number, percent: number) => {
        trackVideoEvent('resumed', {
            ...getVideoMeta(),
            video_current_time: currentTime,
            video_percent: percent,
        });
        console.log('[PostHog] Video resumed:', videoTitle);
    }, [getVideoMeta, videoTitle]);

    useEffect(() => {
        // Wait for player to be ready
        const player = playerRef.current;
        if (!player || !autoTrack) return;

        console.log('[PostHog] Setting up Vimeo tracking for:', videoTitle);

        // Get video duration
        player.getDuration().then((d: number) => {
            duration.current = d;
        }).catch(() => {
            // Duration not available yet
        });

        // Track play/resume
        const handlePlay = () => {
            if (!wasPlaying.current) {
                trackVideoStart();
                wasPlaying.current = true;
            } else {
                player.getCurrentTime().then((seconds: number) => {
                    const percent = (seconds / duration.current) * 100;
                    trackVideoResume(seconds, percent);
                });
            }
        };

        // Track pause
        const handlePause = (data: { seconds: number }) => {
            const percent = (data.seconds / duration.current) * 100;
            trackVideoPause(data.seconds, percent);
        };

        // Track progress milestones
        const handleTimeUpdate = (data: { percent: number; seconds: number }) => {
            const percentComplete = data.percent * 100;

            progressMilestones.forEach(milestone => {
                if (
                    percentComplete >= milestone &&
                    !milestonesTracked.current.has(milestone)
                ) {
                    milestonesTracked.current.add(milestone);
                    trackVideoProgress(milestone, data.seconds);
                }
            });
        };

        // Track completion
        const handleEnded = () => {
            trackVideoComplete();
            wasPlaying.current = false;
        };

        // Attach event listeners
        player.on('play', handlePlay);
        player.on('pause', handlePause);
        player.on('timeupdate', handleTimeUpdate);
        player.on('ended', handleEnded);

        setIsTracking(true);
        console.log('[PostHog] Vimeo tracking initialized for:', videoTitle);

        // Cleanup
        return () => {
            player.off('play', handlePlay);
            player.off('pause', handlePause);
            player.off('timeupdate', handleTimeUpdate);
            player.off('ended', handleEnded);
            setIsTracking(false);
        };
    }, [playerRef, autoTrack, progressMilestones, videoTitle, isAutoplay, trackVideoStart, trackVideoProgress, trackVideoComplete, trackVideoPause, trackVideoResume]);

    return {
        trackVideoStart,
        trackVideoProgress,
        trackVideoComplete,
        trackVideoPause,
        trackVideoResume,
        isTracking,
    };
};

/**
 * Manual video tracking functions for PostHog
 */
export const trackPostHogVideoStart = (videoData: {
    video_title?: string;
    video_provider?: string;
    video_url?: string;
    video_duration?: number;
    playback_type?: 'autoplay' | 'manual';
}) => {
    trackVideoEvent('started', {
        ...videoData,
        video_percent: 0,
        ...getPageContext(),
    });
};

export const trackPostHogVideoProgress = (
    percent: number,
    videoData: {
        video_title?: string;
        video_provider?: string;
        video_url?: string;
        video_duration?: number;
        video_current_time?: number;
    }
) => {
    trackVideoEvent('progress', {
        ...videoData,
        video_percent: percent,
        ...getPageContext(),
    });
};

export const trackPostHogVideoComplete = (videoData: {
    video_title?: string;
    video_provider?: string;
    video_url?: string;
    video_duration?: number;
}) => {
    trackVideoEvent('completed', {
        ...videoData,
        video_percent: 100,
        ...getPageContext(),
    });
};

export const trackPostHogVideoPause = (videoData: {
    video_title?: string;
    video_provider?: string;
    video_url?: string;
    video_duration?: number;
    video_current_time?: number;
    video_percent?: number;
}) => {
    trackVideoEvent('paused', {
        ...videoData,
        ...getPageContext(),
    });
};

export const trackPostHogVideoResume = (videoData: {
    video_title?: string;
    video_provider?: string;
    video_url?: string;
    video_duration?: number;
    video_current_time?: number;
    video_percent?: number;
}) => {
    trackVideoEvent('resumed', {
        ...videoData,
        ...getPageContext(),
    });
};
